import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { RequirePermission } from '@/components/RequirePermission';
import { useLanguage } from '@/i18n/LanguageContext';
import { roleLabel } from '@/i18n/roleLabel';
import { formatDate } from '@/i18n/format';
import { OFFICE_ROLES, type OfficeRole, type OneTimeLink, type StaffInvitation } from '@/api/types';
import { ROLE_HELP, copyText, describeStaffError, oneTimeUrl } from './staff';

/**
 * ADR-084 — Users & Roles: the owner invites employees (one-time link, handed
 * over by the administrator - no email is sent), sees invitations and staff,
 * and opens an employee to change access. The UI hides what the caller may
 * not do; the server enforces it (USERS.WRITE, warehouse scope, no self-change,
 * last ADMIN).
 */
export function UsersPage(): JSX.Element {
  const { t, locale } = useLanguage();
  const { state } = useAuth();
  const members = useApi(() => endpoints.users(), []);
  const invitations = useApi(() => endpoints.invitations(), []);
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<(OneTimeLink & { forWhom: string }) | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const selfId = state.status === 'authenticated' ? state.self.membership_id : null;
  const scopedName = state.status === 'authenticated' && state.self.warehouse_id ? state.self.warehouse_name ?? '' : null;

  async function reissue(inv: StaffInvitation): Promise<void> {
    setActionError(null);
    try {
      const r = await endpoints.reissueInvitation(inv.id);
      setLink({ ...r, forWhom: inv.display_name });
      invitations.reload();
    } catch (err) {
      setActionError(err);
    }
  }
  async function revoke(inv: StaffInvitation): Promise<void> {
    if (!window.confirm(t('staff.confirmRevokeInvite', { name: inv.display_name }))) return;
    setActionError(null);
    try {
      await endpoints.revokeInvitation(inv.id);
      invitations.reload();
    } catch (err) {
      setActionError(err);
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('nav.users')}</h1>
          <div className="sub">{scopedName !== null ? t('staff.subtitleScoped', { warehouse: scopedName }) : t('staff.subtitle')}</div>
        </div>
        <RequirePermission permission="USERS.WRITE">
          {!inviting && (
            <button type="button" className="primary" onClick={() => { setLink(null); setInviting(true); }}>
              {t('staff.invite')}
            </button>
          )}
        </RequirePermission>
      </div>

      {link && <OneTimeLinkPanel kind="invite" link={link} onClose={() => setLink(null)} />}
      {inviting && (
        <InviteForm
          onCancel={() => setInviting(false)}
          onCreated={(l) => {
            setInviting(false);
            setLink(l);
            invitations.reload();
          }}
        />
      )}
      {actionError !== null && <div className="error-banner" role="alert">{describeStaffError(t, actionError) ?? t('staff.err.checkFields')}</div>}

      <RoleGuide />

      <section className="card" aria-labelledby="inv-title">
        <h2 id="inv-title" className="card-title">{t('staff.invitations')}</h2>
        {invitations.status === 'loading' && <LoadingState />}
        {invitations.status === 'error' && <ErrorBanner error={invitations.error} onRetry={invitations.reload} />}
        {invitations.status === 'success' &&
          (invitations.data.invitations.length === 0 ? (
            <p className="sub">{t('staff.noInvitations')}</p>
          ) : (
            <ul className="staff-list">
              {invitations.data.invitations.map((i) => (
                <li key={i.id}>
                  <div className="staff-main">
                    <strong>{i.display_name}</strong> <span className="sub">{i.email}</span>
                    <div className="sub">
                      {roleLabel(t, i.role)} · {i.warehouse_name ?? t('settings.orgWide')}
                      {i.status === 'PENDING' ? ` · ${t('staff.expiresAt', { date: formatDate(i.expires_at, locale) })}` : ''}
                    </div>
                  </div>
                  <span className={`badge ${i.status === 'PENDING' ? 'warn' : i.status === 'ACCEPTED' ? 'ok' : 'neutral'}`}>
                    {t(`staff.inviteStatus.${i.status}` as 'staff.inviteStatus.PENDING')}
                  </span>
                  <RequirePermission permission="USERS.WRITE">
                    {(i.status === 'PENDING' || i.status === 'EXPIRED') && (
                      <div className="staff-actions">
                        <button type="button" onClick={() => void reissue(i)}>{t('staff.newLink')}</button>
                        {i.status === 'PENDING' && (
                          <button type="button" className="danger" onClick={() => void revoke(i)}>{t('staff.revokeInvite')}</button>
                        )}
                      </div>
                    )}
                  </RequirePermission>
                </li>
              ))}
            </ul>
          ))}
      </section>

      <section className="card" aria-labelledby="members-title">
        <h2 id="members-title" className="card-title">{t('staff.members')}</h2>
        {members.status === 'loading' && <LoadingState />}
        {members.status === 'error' && <ErrorBanner error={members.error} onRetry={members.reload} />}
        {members.status === 'success' &&
          (members.data.members.length === 0 ? (
            <EmptyState title={t('users.noMembersYet')} />
          ) : (
            <ul className="staff-list">
              {members.data.members.map((m) => (
                <li key={m.id}>
                  <div className="staff-main">
                    <Link to={`/users/${m.id}`} className="staff-name">
                      {m.display_name ?? m.email}
                      {m.id === selfId && <span className="sub"> ({t('staff.you')})</span>}
                    </Link>
                    <div className="sub">{m.email}</div>
                    <div className="sub">
                      {roleLabel(t, m.role)} · {(m.warehouse_name as string | null | undefined) ?? (m.warehouse_id ? t('staff.oneWarehouse') : t('settings.orgWide'))}
                    </div>
                  </div>
                  <StatusBadge value={m.state} />
                </li>
              ))}
            </ul>
          ))}
      </section>
    </div>
  );
}

function RoleGuide(): JSX.Element {
  const { t } = useLanguage();
  return (
    <details className="card role-guide">
      <summary>{t('staff.roleGuide')}</summary>
      <dl className="role-list">
        {OFFICE_ROLES.map((r) => (
          <div key={r}>
            <dt>{roleLabel(t, r)}</dt>
            <dd>{t(ROLE_HELP[r])}</dd>
          </div>
        ))}
      </dl>
      <p className="field-hint">{t('staff.scopeHelp')}</p>
    </details>
  );
}

export function OneTimeLinkPanel({ kind, link, onClose }: { kind: 'invite' | 'recover'; link: OneTimeLink & { forWhom: string }; onClose: () => void }): JSX.Element {
  const { t, locale } = useLanguage();
  const url = oneTimeUrl(kind, link.token);
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <section className="card success-card" aria-labelledby="link-title" role="status">
      <h2 id="link-title" className="card-title">
        {kind === 'invite' ? t('staff.linkReadyInvite', { name: link.forWhom }) : t('staff.linkReadyRecover', { name: link.forWhom })}
      </h2>
      <div className="code-field">
        <input readOnly value={url} aria-label={t('staff.linkLabel')} onFocus={(e) => e.currentTarget.select()} className="mono" />
        <button type="button" className="primary" onClick={() => void copyText(url).then(setCopied)}>
          {t('staff.copy')}
        </button>
      </div>
      {copied === true && <p className="field-hint ok" aria-live="polite">{t('staff.copied')}</p>}
      {copied === false && <p className="field-hint warn" aria-live="polite">{t('staff.copyManually')}</p>}
      <ul className="done-list">
        <li>{t('staff.linkValid', { date: formatDate(link.expiresAt, locale) })}</li>
        <li>{t('staff.linkOnce')}</li>
        <li>{t('staff.linkHandOver')}</li>
        <li>{t('staff.linkNotProof')}</li>
      </ul>
      <div className="form-actions">
        <button type="button" onClick={onClose}>{t('staff.linkDone')}</button>
      </div>
    </section>
  );
}

function InviteForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (l: OneTimeLink & { forWhom: string }) => void }): JSX.Element {
  const { t } = useLanguage();
  const { state } = useAuth();
  const self = state.status === 'authenticated' ? state.self : null;
  const scopedWh = self?.warehouse_id ?? null;
  const warehouses = useApi(() => endpoints.warehouseSetup(), []);
  const [step, setStep] = useState<'edit' | 'review'>('edit');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OfficeRole>('PICKER');
  const [warehouseId, setWarehouseId] = useState<string>(scopedWh ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const whList = warehouses.status === 'success' ? warehouses.data.warehouses : [];
  const whName = warehouseId ? whList.find((w) => w.id === warehouseId)?.name ?? self?.warehouse_name ?? '' : t('settings.orgWide');
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function create(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await endpoints.invite({ email: email.trim(), displayName: name.trim(), role, warehouseId: warehouseId || null });
      onCreated({ ...r, forWhom: name.trim() });
    } catch (err) {
      setError(err);
      setStep('edit');
    } finally {
      setBusy(false);
    }
  }

  const errText = error ? describeStaffError(t, error) : null;
  return (
    <form
      className="card wh-form"
      aria-labelledby="invite-title"
      onSubmit={(e) => {
        e.preventDefault();
        if (step === 'edit') setStep('review');
        else void create();
      }}
    >
      <h2 id="invite-title" className="card-title">{t('staff.inviteTitle')}</h2>
      {step === 'edit' ? (
        <>
          <div className="form-grid">
            <div className="field-row">
              <label htmlFor="inv-name">{t('staff.name')}</label>
              <input id="inv-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoComplete="off" />
            </div>
            <div className="field-row">
              <label htmlFor="inv-email">{t('login.email')}</label>
              <input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" aria-describedby="inv-email-hint" />
              <p id="inv-email-hint" className="field-hint">{t('staff.emailHint')}</p>
            </div>
          </div>
          <fieldset className="purpose-choice">
            <legend>{t('staff.role')}</legend>
            {OFFICE_ROLES.map((r) => (
              <label key={r} className={`purpose-option${role === r ? ' selected' : ''}`}>
                <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} />
                <span>
                  <strong>{roleLabel(t, r)}</strong>
                  <span className="field-hint">{t(ROLE_HELP[r])}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="field-row">
            <label htmlFor="inv-wh">{t('staff.access')}</label>
            {scopedWh ? (
              <p id="inv-wh" className="sub">{t('staff.scopedOnly', { warehouse: self?.warehouse_name ?? '' })}</p>
            ) : (
              <select id="inv-wh" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} aria-describedby="inv-wh-hint">
                <option value="">{t('staff.wholeCompany')}</option>
                {whList.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}
            <p id="inv-wh-hint" className="field-hint">{t('staff.accessHint')}</p>
          </div>
          {errText !== null && <div className="error-banner" role="alert">{errText}</div>}
          {error !== null && errText === null && <ErrorBanner error={error} />}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={!name.trim() || !emailOk}>{t('staff.review')}</button>
            <button type="button" onClick={onCancel}>{t('common.cancel')}</button>
          </div>
        </>
      ) : (
        <>
          <dl className="kv">
            <dt>{t('staff.name')}</dt>
            <dd>{name.trim()}</dd>
            <dt>{t('login.email')}</dt>
            <dd>{email.trim()}</dd>
            <dt>{t('staff.role')}</dt>
            <dd>{roleLabel(t, role)}</dd>
            <dt>{t('staff.access')}</dt>
            <dd>{whName}</dd>
          </dl>
          <p className="field-hint">{t(ROLE_HELP[role])}</p>
          <p className="notice">{t('staff.reviewNote')}</p>
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy}>{busy ? t('warehouses.creating') : t('staff.createInvite')}</button>
            <button type="button" onClick={() => setStep('edit')} disabled={busy}>{t('staff.edit')}</button>
          </div>
        </>
      )}
    </form>
  );
}
